import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SEO from '../components/SEO';
import {
  BriefcaseIcon,
  AcademicCapIcon,
  TrophyIcon,
  DocumentArrowDownIcon,
  CalendarIcon,
  MapPinIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';

interface Experience {
  id: string;
  title: string;
  company: string;
  location: string;
  startDate: string;
  endDate: string;
  description: string[];
  technologies: string[];
}

interface Education {
  id: string;
  degree: string;
  institution: string;
  location: string;
  year: string;
  description: string;
}

interface Skill {
  category: string;
  skills: { name: string; level: number }[];
}

const Resume: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('experience');
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const experiences: Experience[] = [
    {
      id: '1',
      title: t('resume.experience.exp1.title'),
      company: t('resume.experience.exp1.company'),
      location: t('resume.experience.exp1.location'),
      startDate: t('resume.experience.exp1.startDate'),
      endDate: t('resume.experience.exp1.endDate'),
      description: [
        t('resume.experience.exp1.desc1'),
        t('resume.experience.exp1.desc2'),
        t('resume.experience.exp1.desc3'),
        t('resume.experience.exp1.desc4'),
        t('resume.experience.exp1.desc5')
      ],
      technologies: ['Salesforce', 'PowerAutomate', 'Data Analysis', 'Python', 'Excel']
    },
    {
      id: '2',
      title: t('resume.experience.exp2.title'),
      company: t('resume.experience.exp2.company'),
      location: t('resume.experience.exp2.location'),
      startDate: t('resume.experience.exp2.startDate'),
      endDate: t('resume.experience.exp2.endDate'),
      description: [
        t('resume.experience.exp2.desc1'),
        t('resume.experience.exp2.desc2'),
        t('resume.experience.exp2.desc3'),
        t('resume.experience.exp2.desc4')
      ],
      technologies: ['MEDDIC', 'B2B Sales', 'Marketing Strategy', 'Team Training']
    },
    {
      id: '3',
      title: t('resume.experience.exp3.title'),
      company: t('resume.experience.exp3.company'),
      location: t('resume.experience.exp3.location'),
      startDate: t('resume.experience.exp3.startDate'),
      endDate: t('resume.experience.exp3.endDate'),
      description: [
        t('resume.experience.exp3.desc1'),
        t('resume.experience.exp3.desc2'),
        t('resume.experience.exp3.desc3')
      ],
      technologies: ['SEO', 'Facebook Ads', 'Pinterest Ads', 'Influencer Marketing', 'Data Analysis']
    },
    {
      id: '4',
      title: t('resume.experience.exp4.title'),
      company: t('resume.experience.exp4.company'),
      location: t('resume.experience.exp4.location'),
      startDate: t('resume.experience.exp4.startDate'),
      endDate: t('resume.experience.exp4.endDate'),
      description: [
        t('resume.experience.exp4.desc1'),
        t('resume.experience.exp4.desc2')
      ],
      technologies: ['B2C Sales', 'Customer Service', 'Store Management']
    }
  ];

  const education: Education[] = [
    {
      id: '1',
      degree: t('resume.education.edu1.degree'),
      institution: t('resume.education.edu1.institution'),
      location: t('resume.education.edu1.location'),
      year: t('resume.education.edu1.year'),
      description: t('resume.education.edu1.description')
    },
    {
      id: '2',
      degree: t('resume.education.edu2.degree'),
      institution: t('resume.education.edu2.institution'),
      location: t('resume.education.edu2.location'),
      year: t('resume.education.edu2.year'),
      description: t('resume.education.edu2.description')
    }
  ];

  const skills: Skill[] = [
    {
      category: t('resume.skills.programming.category'),
      skills: [
        { name: 'Python', level: 95 },
        { name: 'JavaScript/TypeScript', level: 90 },
        { name: 'Java', level: 85 },
        { name: 'SQL', level: 90 },
        { name: 'R', level: 80 }
      ]
    },
    {
      category: t('resume.skills.frameworks.category'),
      skills: [
        { name: 'React/Next.js', level: 90 },
        { name: 'Node.js/Express', level: 85 },
        { name: 'TensorFlow/PyTorch', level: 88 },
        { name: 'Django/FastAPI', level: 85 },
        { name: 'Spring Boot', level: 80 }
      ]
    },
    {
      category: t('resume.skills.tools.category'),
      skills: [
        { name: 'AWS/Azure/GCP', level: 85 },
        { name: 'Docker/Kubernetes', level: 80 },
        { name: 'Git/GitHub', level: 95 },
        { name: 'Apache Airflow', level: 85 },
        { name: 'Tableau/Power BI', level: 80 }
      ]
    },
    {
      category: t('resume.skills.languages.category'),
      skills: [
        { name: t('resume.skills.languages.english'), level: 100 },
        { name: t('resume.skills.languages.french'), level: 100 },
        { name: t('resume.skills.languages.spanish'), level: 85 },
        { name: t('resume.skills.languages.portuguese'), level: 75 }
      ]
    }
  ];

  const certifications = [
    {
      id: '1',
      name: t('resume.certifications.cert1.name'),
      issuer: t('resume.certifications.cert1.issuer'),
      year: t('resume.certifications.cert1.year')
    },
    {
      id: '2',
      name: t('resume.certifications.cert2.name'),
      issuer: t('resume.certifications.cert2.issuer'),
      year: t('resume.certifications.cert2.year')
    },
    {
      id: '3',
      name: t('resume.certifications.cert3.name'),
      issuer: t('resume.certifications.cert3.issuer'),
      year: t('resume.certifications.cert3.year')
    }
  ];

  const tabs = [
    { id: 'experience', name: t('resume.tabs.experience'), icon: BriefcaseIcon },
    { id: 'education', name: t('resume.tabs.education'), icon: AcademicCapIcon },
    { id: 'skills', name: t('resume.tabs.skills'), icon: TrophyIcon }
  ];

  const renderExperience = () => (
    <div className="space-y-8">
      {experiences.map((exp, index) => (
        <motion.div
          key={exp.id}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: index * 0.1 }}
          className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-gray-600 transition-colors"
        >
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-4">
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-white mb-2">
                {exp.title}
              </h3>
              <div className="flex items-center space-x-4 text-gray-300 mb-2">
                <div className="flex items-center space-x-2">
                  <BuildingOfficeIcon className="w-4 h-4 text-primary-400" />
                  <span>{exp.company}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPinIcon className="w-4 h-4 text-primary-400" />
                  <span>{exp.location}</span>
                </div>
              </div>
              <div className="flex items-center space-x-2 text-gray-400 mb-4">
                <CalendarIcon className="w-4 h-4" />
                <span>{exp.startDate} - {exp.endDate}</span>
              </div>
            </div>
          </div>
          
          <ul className="space-y-2 mb-4">
            {exp.description.map((desc, descIndex) => (
              <li key={descIndex} className="text-gray-300 flex items-start space-x-2">
                <span className="text-primary-400 mt-2">•</span>
                <span>{desc}</span>
              </li>
            ))}
          </ul>
          
          <div className="flex flex-wrap gap-2">
            {exp.technologies.map((tech) => (
              <span
                key={tech}
                className="px-3 py-1 bg-primary-500/10 border border-primary-500/20 text-primary-300 rounded-full text-sm"
              >
                {tech}
              </span>
            ))}
          </div>
        </motion.div>
      ))}
    </div>
  );

  const renderEducation = () => (
    <div className="space-y-6">
      {education.map((edu, index) => (
        <motion.div
          key={edu.id}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: index * 0.1 }}
          className="bg-gray-800 border border-gray-700 rounded-xl p-6 hover:border-gray-600 transition-colors"
        >
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between">
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-white mb-2">
                {edu.degree}
              </h3>
              <div className="flex items-center space-x-4 text-gray-300 mb-2">
                <div className="flex items-center space-x-2">
                  <AcademicCapIcon className="w-4 h-4 text-primary-400" />
                  <span>{edu.institution}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPinIcon className="w-4 h-4 text-primary-400" />
                  <span>{edu.location}</span>
                </div>
              </div>
              <div className="flex items-center space-x-2 text-gray-400 mb-4">
                <CalendarIcon className="w-4 h-4" />
                <span>{edu.year}</span>
              </div>
              <p className="text-gray-300">{edu.description}</p>
            </div>
          </div>
        </motion.div>
      ))}
      
      {/* Certifications */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="bg-gray-800 border border-gray-700 rounded-xl p-6"
      >
        <h3 className="text-xl font-semibold text-white mb-6 flex items-center space-x-2">
          <TrophyIcon className="w-6 h-6 text-primary-400" />
          <span>{t('resume.certifications.title')}</span>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {certifications.map((cert, index) => (
            <motion.div
              key={cert.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.4, delay: 0.1 * index }}
              className="bg-gray-700/50 border border-gray-600 rounded-lg p-4"
            >
              <h4 className="text-white font-medium mb-2">{cert.name}</h4>
              <p className="text-gray-300 text-sm mb-1">{cert.issuer}</p>
              <p className="text-gray-400 text-xs">{cert.year}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );

  const renderSkills = () => (
    <div className="space-y-8">
      {skills.map((skillCategory, categoryIndex) => (
        <motion.div
          key={skillCategory.category}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
          className="bg-gray-800 border border-gray-700 rounded-xl p-6"
        >
          <h3 className="text-xl font-semibold text-white mb-6">
            {skillCategory.category}
          </h3>
          
          <div className="space-y-4">
            {skillCategory.skills.map((skill, skillIndex) => (
              <motion.div
                key={skill.name}
                initial={{ opacity: 0, x: -20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.4, delay: (categoryIndex * 0.1) + (skillIndex * 0.05) }}
                className="space-y-2"
              >
                <div className="flex justify-between items-center">
                  <span className="text-gray-300 font-medium">{skill.name}</span>
                  <span className="text-primary-400 text-sm">{skill.level}%</span>
                </div>
                
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <motion.div
                    className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full"
                    initial={{ width: 0 }}
                    animate={inView ? { width: `${skill.level}%` } : { width: 0 }}
                    transition={{ duration: 1, delay: (categoryIndex * 0.1) + (skillIndex * 0.05) + 0.2 }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      ))}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'experience':
        return renderExperience();
      case 'education':
        return renderEducation();
      case 'skills':
        return renderSkills();
      default:
        return renderExperience();
    }
  };

  return (
    <>
      <SEO 
        title={t('resume.title')}
        description={t('resume.subtitle')}
      />
      <div className="space-y-12">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center"
      >
        <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
          {t('resume.title')}
        </h1>
        <p className="text-lg text-gray-300 max-w-3xl mx-auto mb-8">
          {t('resume.subtitle')}
        </p>
        
        <button 
          className="btn-primary flex items-center space-x-2 mx-auto"
          onClick={() => {
            // Create a print-friendly version
            window.print();
          }}
          title={t('resume.downloadPdf')}
        >
          <DocumentArrowDownIcon className="w-5 h-5" />
          <span>{t('resume.downloadPdf')}</span>
        </button>
      </motion.header>

      {/* Tabs */}
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="flex justify-center"
      >
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-2 flex space-x-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-all duration-300 ${
                  activeTab === tab.id
                    ? 'bg-primary-500 text-white'
                    : 'text-gray-300 hover:text-white hover:bg-gray-700'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{tab.name}</span>
              </button>
            );
          })}
        </div>
      </motion.div>

      {/* Tab Content */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        {renderTabContent()}
      </motion.div>

      {/* Summary Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="grid grid-cols-2 md:grid-cols-4 gap-6"
      >
        {[
          { number: '8+', label: t('resume.stats.experience') },
          { number: '50+', label: t('resume.stats.projects') },
          { number: '15+', label: t('resume.stats.technologies') },
          { number: '4', label: t('resume.stats.languages') }
        ].map((stat, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.4, delay: 0.1 * index }}
            className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 text-center"
          >
            <div className="text-3xl lg:text-4xl font-bold text-primary-400 mb-2">
              {stat.number}
            </div>
            <div className="text-gray-300 text-sm">
              {stat.label}
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
    </>
  );
};

export default Resume;